import { createContext, forwardRef, useContext, useId } from 'react'

import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form'

import { cn } from '../utils'
import { Label } from './Label'
import { Skeleton } from './Skeleton'

const Form = FormProvider

type TFormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}

const FormFieldContext = createContext<TFormFieldContextValue>({} as TFormFieldContextValue)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = useContext(FormFieldContext)
  const itemContext = useContext(FormItemContext)
  const { formState, getFieldState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>')
  }

  const { id } = itemContext

  return {
    formDescriptionId: `${id}-form-item-description`,
    formItemId: `${id}-form-item`,
    formMessageId: `${id}-form-item-message`,
    id,
    name: fieldContext.name,
    ...fieldState,
  }
}

type TFormItemContextValue = {
  id: string
}

const FormItemContext = createContext<TFormItemContextValue>({} as TFormItemContextValue)

interface IFormItemProps extends React.HTMLAttributes<HTMLDivElement> {
  layout?: 'horizontal' | 'vertical'
}

const FormItem = forwardRef<HTMLDivElement, IFormItemProps>(
  ({ className, layout = 'vertical', ...props }, ref) => {
    const id = useId()

    return (
      <FormItemContext.Provider value={{ id }}>
        <div
          className={cn(
            'w-full',
            layout === 'vertical' ? 'space-y-2' : 'flex items-center justify-between',
            className
          )}
          ref={ref}
          {...props}
        />
      </FormItemContext.Provider>
    )
  }
)
FormItem.displayName = 'FormItem'

const FormLabel = forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField()

  return (
    <Label
      className={cn(
        'text-text/80 inline-flex items-center gap-2 whitespace-nowrap text-sm',
        error && 'text-destructive',
        className
      )}
      htmlFor={formItemId}
      ref={ref}
      {...props}
    />
  )
})
FormLabel.displayName = 'FormLabel'

const FormControl = forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formDescriptionId, formItemId, formMessageId } = useFormField()

  return (
    <Slot
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      id={formItemId}
      ref={ref}
      {...props}
    />
  )
})
FormControl.displayName = 'FormControl'

const FormDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField()

  return (
    <p
      className={cn('text-muted-foreground text-[0.8rem]', className)}
      id={formDescriptionId}
      ref={ref}
      {...props}
    />
  )
})
FormDescription.displayName = 'FormDescription'

const FormMessage = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ children, className, ...props }, ref) => {
    const { error, formMessageId } = useFormField()
    const body = error ? String(error?.message) : children

    if (!body) {
      return null
    }

    return (
      <p
        className={cn('text-red-500 text-[0.8rem] font-medium', className)}
        id={formMessageId}
        ref={ref}
        {...props}
      >
        {body}
      </p>
    )
  }
)
FormMessage.displayName = 'FormMessage'

const FormFieldRow = ({
  className,
  icon,
  input,
  label,
}: {
  className?: string
  icon?: React.ComponentType<{ className?: string }>
  input: React.ReactNode
  label: string
}) => {
  const Icon = icon

  return (
    <div className={cn('flex flex-row items-center gap-1.5', className)}>
      <div
        className="flex min-w-[80px] max-w-[125px] w-[40%] min-h-8 flex-shrink-0 items-center gap-1.5
          text-muted-foreground"
      >
        {Icon && <Icon className="size-3" />}
        <FormLabel>{label}</FormLabel>
      </div>
      {input}
    </div>
  )
}

const FormFieldRowSkeleton = ({
  icon,
  label,
}: {
  icon?: React.ComponentType<{ className?: string }>
  label: string
}) => {
  return (
    <FormFieldRow
      icon={icon}
      input={<Skeleton className="h-6 w-full max-w-[225px] bg-background/80" />}
      label={label}
    />
  )
}

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormFieldRow,
  FormFieldRowSkeleton,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
}
